import { useEffect, useRef, useState } from "react";
import { Button, Icon, Input, Spin, Text, useToast } from "@app/ui";
import { useT } from "../i18n.js";
import { useGoogleMaps } from "./loader.js";

/** ~0.1 m precision — keeps the synced lat/lng inputs readable. */
const round6 = (n: number): number => Math.round(n * 1e6) / 1e6;

/** Türkiye-centered default view — the primary user base. */
const DEFAULT_CENTER = { lat: 39.0, lng: 35.24 };
const DEFAULT_ZOOM = 5;
const PICKED_ZOOM = 12;

/**
 * Click-to-pick location input: a Google map with a draggable marker plus an
 * address search (Geocoder). Controlled by lat/lng so it stays in two-way sync
 * with plain numeric inputs next to it; when maps are disabled (no key) it
 * collapses to a one-line hint and those inputs remain the fallback.
 */
export function MapPicker({
  lat,
  lng,
  onChange,
  disabled = false,
  height = 280,
}: {
  lat: number | null;
  lng: number | null;
  onChange: (lat: number, lng: number) => void;
  disabled?: boolean;
  height?: number;
}) {
  const t = useT();
  const toast = useToast();
  const maps = useGoogleMaps();
  const boxRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  // Listeners are wired once at map init — refs keep them on fresh props.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;
  const valueRef = useRef({ lat, lng });
  valueRef.current = { lat, lng };
  const [ready, setReady] = useState(false);
  const [q, setQ] = useState("");
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!maps || !boxRef.current) return;
    const { lat: lat0, lng: lng0 } = valueRef.current;
    const hasValue = lat0 !== null && lng0 !== null;
    const map = new maps.Map(boxRef.current, {
      center: hasValue ? { lat: lat0, lng: lng0 } : DEFAULT_CENTER,
      zoom: hasValue ? PICKED_ZOOM : DEFAULT_ZOOM,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      // The picker lives inside a modal — plain wheel/drag zoom beats the
      // ctrl+scroll dance there.
      gestureHandling: "greedy",
    });
    map.addListener("click", (e: google.maps.MapMouseEvent) => {
      if (disabledRef.current || !e.latLng) return;
      onChangeRef.current(round6(e.latLng.lat()), round6(e.latLng.lng()));
    });
    mapRef.current = map;
    setReady(true);
    return () => {
      markerRef.current?.setMap(null);
      markerRef.current = null;
      mapRef.current = null;
      setReady(false);
    };
  }, [maps]);

  // Marker follows the controlled value (map click, drag, search — or the
  // sibling numeric inputs).
  useEffect(() => {
    const map = mapRef.current;
    if (!maps || !map || !ready) return;
    if (lat === null || lng === null) {
      markerRef.current?.setMap(null);
      markerRef.current = null;
      return;
    }
    const pos = { lat, lng };
    if (!markerRef.current) {
      const marker = new maps.Marker({ map, position: pos, draggable: !disabled });
      marker.addListener("dragend", () => {
        const p = marker.getPosition();
        if (p) onChangeRef.current(round6(p.lat()), round6(p.lng()));
      });
      markerRef.current = marker;
      map.panTo(pos);
      if ((map.getZoom() ?? 0) < 8) map.setZoom(PICKED_ZOOM);
    } else {
      markerRef.current.setPosition(pos);
      markerRef.current.setDraggable(!disabled);
      if (!map.getBounds()?.contains(pos)) map.panTo(pos);
    }
  }, [maps, ready, lat, lng, disabled]);

  const search = async () => {
    const query = q.trim();
    if (!maps || searching || query === "") return;
    setSearching(true);
    try {
      const res = await new maps.Geocoder().geocode({ address: query });
      const hit = res.results[0];
      if (!hit) {
        toast.info(t("map.noResult"));
        return;
      }
      const loc = hit.geometry.location;
      onChange(round6(loc.lat()), round6(loc.lng()));
      mapRef.current?.fitBounds(hit.geometry.viewport);
    } catch {
      // The Geocoder rejects on ZERO_RESULTS too — same user-facing outcome.
      toast.info(t("map.noResult"));
    } finally {
      setSearching(false);
    }
  };

  if (maps === null) {
    return (
      <Text small secondary>
        {t("map.unavailable")}
      </Text>
    );
  }

  return (
    <div className="map-picker">
      {!disabled && (
        <div className="map-search">
          <Input
            placeholder={t("map.searchPlaceholder")}
            value={q}
            disabled={!ready}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void search();
              }
            }}
          />
          <Button
            aria-label={t("map.search")}
            icon={<Icon name="search" size={14} />}
            loading={searching}
            disabled={!ready}
            onClick={() => void search()}
          />
        </div>
      )}
      <div className="map-canvas" style={{ height }}>
        <div ref={boxRef} className="map-fill" />
        {maps === undefined && (
          <div className="map-loading">
            <Spin />
          </div>
        )}
      </div>
      {!disabled && (
        <Text small secondary>
          {t("map.pickHint")}
        </Text>
      )}
    </div>
  );
}
